/**
 * Citation Processor
 *
 * Handles extraction and formatting of citations from markdown content
 * Based on the legacy implementation patterns from Answer component
 */
import type { Citation, ProcessedContent } from '../types';

export const CITATION_CONSTANTS = {
  SEPARATOR: '::',
  PREFIX: '__CITATION__',
  BRACKET_REGEX: /\[([^\]]+)\](\(__CITATION__\d+\))?/g,
  MARKDOWN_LINK_REGEX: /\[([^\]]+)\]\(([^)]+)\)/g,
  SOURCE_PREFIX_REGEX: /Source:+\s*/i,
  SOURCES_SECTION_REGEX: /Sources:\s*\n((?:\d+\.\s*[^\n]+\n?)*)/g,
  XML_INLINE_REGEX: /<coopgptsourceref\s*>(.*?)<\/coopgptsourceref>/gi, // Only tags without attributes (just whitespace)
  XML_INLINE_WITH_ATTRS_REGEX: /<coopgptsourceref\s+([^>]+)>([\s\S]*?)<\/coopgptsourceref>/gi, // Only tags with attributes (space + content)
  XML_BLOCK_REGEX: /<allcoopgptcitations>([\s\S]*?)<\/allcoopgptcitations>/i,
  XML_ENTRY_REGEX: /<coopgptcitationentry\s*>([\s\S]*?)<\/coopgptcitationentry>/gi,
  XML_ENTRY_WITH_ATTRS_REGEX: /<coopgptcitationentry\b([^>]*)>([\s\S]*?)<\/coopgptcitationentry>/gi,
  SOURCEREF_ID_REGEX: /<sourceref\s+id=["'](\d+)["'][^>]*>([\s\S]*?)<\/sourceref>/gi,
  XML_ID_URL_REGEX: /<coopgptcitations\b[^>]*\sid=["'](\d+)["'][^>]*>([\s\S]*?)<\/coopgptcitations>/gi,
  ATTR_ID_REGEX: /\bid=(?:["'](\d+)["']|(\d+))/i,
  ATTR_URL_REGEX: /\burl=(?:["']([^"']+)["']|([^\s>]+))/i,
  CITATIONS_SECTION_REGEX: /Citations:\s*\n((?:\d+\.\s*[^\n]+\n?)*)/gi,
} as const;

export function processCitations(content: string): ProcessedContent {
  const citationByIndex: Record<number, Citation> = {};
  const nameToIndex = new Map<string, number>();
  /** Deduplication map: canonical URL → citation index already allocated. */
  const urlToIndex = new Map<string, number>();
  const idToUrl = new Map<number, string>();
  let citationCounter = 1;
  let processedContent = content;

  const normalize = (text: string) =>
    text.replace(CITATION_CONSTANTS.SOURCE_PREFIX_REGEX, '')
        .replace(/\s+/g, ' ')
        .trim()
        .toLowerCase()
        .replace(/^(\d+)\.\s*/, (_: string, num) => `${parseInt(num)}. `)
        .replace(/^(\d+)\s+/, (_: string, num) => `${parseInt(num)}. `);

  const getKey = (text: string, url?: string) => {
    const normalizedText = normalize(text);
    return url ? `${normalizedText}${CITATION_CONSTANTS.SEPARATOR}${url}` : normalizedText;
  };

  /** Return true when the string looks like a full URL (http/https). */
  const looksLikeUrl = (s: string) => /^https?:\/\//i.test(s.trim());

  /**
   * Get-or-create a citation index, deduplicating by URL.
   * If a citation with the same URL already exists, its index is returned
   * regardless of the display text — this prevents the same document from
   * appearing as multiple numbered citations.
   */
  const getOrCreateCitationIndex = (text: string, url?: string): number => {
    // URL-based dedup: reuse existing citation for the same URL
    if (url) {
      const existing = urlToIndex.get(url);
      if (existing !== undefined) return existing;
    }

    // Text-based dedup (original behaviour as fallback)
    const key = getKey(text, url);
    let index = nameToIndex.get(key);
    if (index) return index;

    // Allocate a new index
    while (citationByIndex[citationCounter]) citationCounter++;
    index = citationCounter++;
    nameToIndex.set(key, index);
    if (url) urlToIndex.set(url, index);
    citationByIndex[index] = { id: `citation-${index}`, text, url, index };
    return index;
  };

  for (const m of processedContent.matchAll(CITATION_CONSTANTS.XML_ID_URL_REGEX)) {
    const id = parseInt(m[1], 10);
    const url = (m[2] || '').trim();
    if (!isNaN(id) && url) idToUrl.set(id, url);
  }
  processedContent = processedContent.replace(CITATION_CONSTANTS.XML_ID_URL_REGEX, '');

  processedContent = processedContent.replace(
    CITATION_CONSTANTS.SOURCEREF_ID_REGEX,
    (_m, idStr: string, name: string) => {
      const id = parseInt(idStr, 10);
      const clean = (name || '').trim();
      if (isNaN(id) || !clean) return '';
      const url = idToUrl.get(id);

      // URL-based dedup: reuse existing citation for the same URL
      if (url) {
        const existingIdx = urlToIndex.get(url);
        if (existingIdx !== undefined) {
          return `${clean}[](${CITATION_CONSTANTS.PREFIX}${existingIdx})`;
        }
      }

      if (!citationByIndex[id]) {
        citationByIndex[id] = { id: `citation-${id}`, text: clean, url, index: id };
        if (url) urlToIndex.set(url, id);
      } else {
        if (!citationByIndex[id].text) citationByIndex[id].text = clean;
        if (!citationByIndex[id].url && url) {
          citationByIndex[id].url = url;
          urlToIndex.set(url, id);
        }
      }
      return `${clean}[](${CITATION_CONSTANTS.PREFIX}${id})`;
    }
  );

  processedContent = processedContent.replace(
    CITATION_CONSTANTS.XML_INLINE_WITH_ATTRS_REGEX,
    (_m, attrs: string, inner: string) => {
      const idMatch = (attrs || '').match(CITATION_CONSTANTS.ATTR_ID_REGEX);
      const urlMatch = (attrs || '').match(CITATION_CONSTANTS.ATTR_URL_REGEX);
      const clean = (inner || '').trim();
      const id = idMatch ? parseInt(idMatch[1] || idMatch[2], 10) : NaN;
      const urlAttr = urlMatch ? (urlMatch[1] || urlMatch[2]) : undefined;
      if (!isNaN(id)) {
        const url = urlAttr || idToUrl.get(id);

        // URL-based dedup: reuse existing citation for the same URL
        if (url) {
          const existingIdx = urlToIndex.get(url);
          if (existingIdx !== undefined) {
            return `[](${CITATION_CONSTANTS.PREFIX}${existingIdx})`;
          }
        }

        // Text-based dedup (fallback)
        const existingCitation = Object.values(citationByIndex).find(c => c.text === clean);
        if (existingCitation) {
          return `[](${CITATION_CONSTANTS.PREFIX}${existingCitation.index})`;
        }

        // Only create citation if we don't already have this ID
        if (!citationByIndex[id]) {
          citationByIndex[id] = { id: `citation-${id}`, text: clean, url, index: id };
          if (url) urlToIndex.set(url, id);
        }
        return `[](${CITATION_CONSTANTS.PREFIX}${id})`;
      }
      const index = getOrCreateCitationIndex(clean);
      return `${clean}[](${CITATION_CONSTANTS.PREFIX}${index})`;
    }
  );

  processedContent = processedContent.replace(
    CITATION_CONSTANTS.XML_INLINE_REGEX,
    (_m, name: string) => {
      const clean = (name || '').trim();
      if (!clean) return '';
      const index = getOrCreateCitationIndex(clean);
      return `${clean}[](${CITATION_CONSTANTS.PREFIX}${index})`;
    }
  );

  const blockMatch = CITATION_CONSTANTS.XML_BLOCK_REGEX.exec(processedContent);
  if (blockMatch) {
    const block = blockMatch[1] || '';

    for (const m of block.matchAll(CITATION_CONSTANTS.XML_ENTRY_WITH_ATTRS_REGEX)) {
      const attrs = m[1] || '';
      const raw = (m[2] || '').trim();
      const idMatch = attrs.match(CITATION_CONSTANTS.ATTR_ID_REGEX);
      const urlMatch = attrs.match(CITATION_CONSTANTS.ATTR_URL_REGEX);
      const id = idMatch ? parseInt(idMatch[1] || idMatch[2], 10) : NaN;
      const name = raw.split(CITATION_CONSTANTS.SEPARATOR)[0]?.trim() || raw;
      const url = (urlMatch ? (urlMatch[1] || urlMatch[2]) : undefined) || raw.split(CITATION_CONSTANTS.SEPARATOR)[1]?.trim() || idToUrl.get(id);
      if (!isNaN(id) && name) {
        // URL-based dedup: skip if this URL already has a citation
        if (url && urlToIndex.has(url)) continue;

        // Text-based dedup (fallback)
        const existingCitation = Object.values(citationByIndex).find(c => c.text === name);
        if (existingCitation) continue;

        // Only create citation if we don't already have this ID
        if (!citationByIndex[id]) {
          citationByIndex[id] = { id: `citation-${id}`, text: name, url: url || undefined, index: id };
          if (url) urlToIndex.set(url, id);
        }
      }
    }

    for (const m of block.matchAll(CITATION_CONSTANTS.XML_ENTRY_REGEX)) {
      const raw = (m[1] || '').trim();
      if (!raw) continue;
      const parts = raw.split(CITATION_CONSTANTS.SEPARATOR);
      const name = parts[0]?.trim();
      const url = parts[1]?.trim();
      if (!name) continue;

      // URL-based dedup: skip if this URL already has a citation
      if (url && urlToIndex.has(url)) continue;

      // Text-based dedup (fallback)
      const existingCitation = Object.values(citationByIndex).find(c => c.text === name);
      if (existingCitation) {
        // Backfill URL if the existing entry lacks one
        if (url && !existingCitation.url) {
          existingCitation.url = url;
          urlToIndex.set(url, existingCitation.index);
        }
        continue;
      }

      const index = getOrCreateCitationIndex(name, url || undefined);
    }

    processedContent = processedContent.replace(CITATION_CONSTANTS.XML_BLOCK_REGEX, '');
  }

  processedContent = processedContent.replace(
    CITATION_CONSTANTS.MARKDOWN_LINK_REGEX,
    (_m, text: string, url: string, offset: number, original: string) => {
      // Skip image syntax ![alt](url) so images render normally
      if (offset > 0 && original[offset - 1] === '!') {
        return _m;
      }
      if (!text) return _m;
      if (url?.startsWith(CITATION_CONSTANTS.PREFIX)) return _m;

      // When the display text is itself a URL, leave as a normal markdown
      // link — converting it would produce  url[](__CITATION__N)  which
      // breaks when remarkGfm auto-links the leading URL.
      if (looksLikeUrl(text)) {
        return _m;
      }

      const index = getOrCreateCitationIndex(text, url);
      return `${text}[](${CITATION_CONSTANTS.PREFIX}${index})`;
    }
  );

  // processedContent = processedContent.replace(
  //   CITATION_CONSTANTS.BRACKET_REGEX,
  //   (_m, label: string, existing: string) => {
  //     if (existing) return _m;
  //     const clean = (label || '').replace(CITATION_CONSTANTS.SOURCE_PREFIX_REGEX, '').trim();
  //     if (!clean) return _m;
  //     const key = getKey(clean);
  //     let index = nameToIndex.get(key);
  //     if (!index) {
  //       while (citationByIndex[citationCounter]) citationCounter++;
  //       index = citationCounter++;
  //       nameToIndex.set(key, index);
  //       citationByIndex[index] = { id: `citation-${index}`, text: clean, index };
  //     }
  //     return `[](${CITATION_CONSTANTS.PREFIX}${index})`;
  //   }
  // );

  // Remove citations section without processing it to avoid duplicates
  processedContent = processedContent.replace(CITATION_CONSTANTS.CITATIONS_SECTION_REGEX, '');

  processedContent = processedContent.replace(
    CITATION_CONSTANTS.SOURCES_SECTION_REGEX,
    ''
  );

  const indices = Object.keys(citationByIndex)
    .map(n => parseInt(n, 10))
    .filter(n => !isNaN(n))
    .sort((a, b) => a - b);
  const citations: Citation[] = indices.map(i => citationByIndex[i]);

  return { content: processedContent, citations, questions: [] };
}
